import * as Sentry from '@sentry/browser'
import { lazy, Suspense, useEffect, useState, useRef, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'

// Actions
import { setFilter } from 'src/actions/filters'
import {
  searchListingsForMapMarkers,
  searchListingsForListView,
} from 'src/actions/listings'

// Constants
import {
  MIN_MAP_ZOOM,
  MAX_MAP_ZOOM,
  CLUSTER_OPTIONS,
  STARTING_ZOOM,
} from 'src/constants/listings'

// State
import { useTheme } from '@homebot/shared-ui-swashbuckle'

// Selectors
import { selectSearchListingMarkers } from 'src/store/selectors/listings'
import { selectCustomerId } from 'src/store/selectors/customerProfile'
import { setSelectedListingId } from 'src/actions/listings'

// Hooks
import { useListingDetailsLocationMapStyles } from 'src/hooks/listings/useMapStyles'
import useSuperCluster from 'src/hooks/useSuperCluster'

// Components
import LineScale from 'src/Components/shared/Loading/LineScale/LineScale'
import Marker from './Marker'
import Cluster from './Cluster'

// Styles
import styles from './ListingsMap.module.scss'

const PropertyMap = lazy(() => import('google-map-react'))

const LoadingContainer = () => (
  <div className={styles.loadingContainer}>
    <LineScale />
  </div>
)

const ListingsMap = ({ filter, focusedMarker, onMarkerClick }) => {
  const dispatch = useDispatch()
  const markers = useSelector(selectSearchListingMarkers)
  const customerId = useSelector(selectCustomerId)
  const googleApiKey = { key: process.env.REACT_APP_GOOGLE_KEY }
  const [googleMapsInternals, setGoogleMapsInternals] = useState(null)
  // Prevents handling the map change event when we first load to prevent an infinite loop. We want to
  // handle subsequent onChange events to effect the search results based on the maps current location,
  // zoom, etc
  const [firstRender, setFirstRender] = useState(true)
  const mapRef = useRef(null)
  const mapRefContainer = useRef(null)

  const [mapState, setMapState] = useState({
    // Map requires a center to start so default to CO
    center: {
      lat: 39.2485842,
      lng: -106.8924116,
    },
    zoom: STARTING_ZOOM,
    bounds: null,
  })
  const { theme } = useTheme()
  const mapStyles = useListingDetailsLocationMapStyles(theme.isLightThemeType)

  // splitting apart this array for useEffect dependencies array
  const [propertyType0, propertyType1, propertyType2, propertyType3] =
    filter?.propertyTypes || [undefined, undefined, undefined, undefined]

  const geoJsonPoints = markers?.map(marker => ({
    type: 'Feature',
    properties: { cluster: false, ...marker },
    geometry: {
      type: 'Point',
      coordinates: [marker.lng, marker.lat],
    },
  }))

  const clusterBounds = useMemo(() => {
    let bounds = []
    if (mapState?.bounds) {
      bounds = [
        mapState?.bounds?.nw?.lng,
        mapState?.bounds?.se?.lat,
        mapState?.bounds?.se?.lng,
        mapState?.bounds?.nw?.lat,
      ]
    }
    return bounds
  }, [mapState?.bounds])

  const { clusters, supercluster } = useSuperCluster({
    geoJsonPoints: geoJsonPoints || [],
    bounds: clusterBounds,
    zoom: mapState?.zoom,
    options: CLUSTER_OPTIONS,
  })

  const onClusterClick = (id, _e) => {
    const clickedCluster = clusters.find(c => c.id === id)
    const [longitude, latitude] = clickedCluster.geometry.coordinates
    const expansionZoom = Math.min(
      supercluster.getClusterExpansionZoom(clickedCluster.id),
      MAX_MAP_ZOOM
    )

    googleMapsInternals.map.setZoom(expansionZoom)
    googleMapsInternals.map.panTo({ lat: latitude, lng: longitude })
  }

  useEffect(() => {
    setFirstRender(true)

    if (googleMapsInternals && googleMapsInternals.map) {
      dispatch(
        searchListingsForMapMarkers(customerId, {
          ...filter,
        })
      ).then(({ listings: searchListings }) => {
        if (searchListings && searchListings.length > 0) {
          const bounds = new window.google.maps.LatLngBounds()

          searchListings.map(listing => {
            const loc = new window.google.maps.LatLng(listing.lat, listing.lng)
            bounds.extend(loc)
            return listing
          })

          googleMapsInternals.map.fitBounds(bounds)
          googleMapsInternals.map.panToBounds(bounds)
        } else {
          const geocoder = new window.google.maps.Geocoder()
          const geocoderRequest = {
            address: filter.location?.name,
            region: 'US',
          }
          geocoder.geocode(geocoderRequest, (results, status) => {
            if (status === 'OK') {
              googleMapsInternals.map.setCenter(results[0].geometry.location)
            } else {
              Sentry.captureMessage(`Geocode failed: ${status}`)
            }
          })
        }

        setMapState({
          ...mapState,
          zoom: googleMapsInternals.map.zoom,
        })
      })
    }
    // We specifically do not want to include filter & mapState in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filter.location?.name,
    filter.location?.id,
    filter.minPriceCents,
    filter.maxPriceCents,
    filter.minBathroomsCount,
    filter.minBedroomsCount,
    filter.minSqft,
    filter.maxSqft,
    filter.minYearBuilt,
    filter.maxYearBuilt,
    propertyType0,
    propertyType1,
    propertyType2,
    propertyType3,
    googleMapsInternals,
    dispatch,
  ])

  const mapOptions = maps => ({
    styles: mapStyles,
    clickableIcons: false,
    mapTypeControl: false,
    minZoom: MIN_MAP_ZOOM,
    maxZoom: MAX_MAP_ZOOM,
    zoomControlOptions: {
      position: maps.ControlPosition.RIGHT_TOP,
    },
    scrollwheel: true,
    disableDefaultUI: true,
    streetViewControl: false,
    scaleControl: true,
    gestureHandling: 'greedy',
    zoomControl: true,
  })

  const handleChange = state => {
    if (!firstRender) {
      const boundingBox = {
        top_left: {
          lat: state.bounds?.nw?.lat,
          lon: state.bounds?.nw?.lng,
        },
        bottom_right: {
          lat: state.bounds?.se?.lat,
          lon: state.bounds?.se?.lng,
        },
      }

      dispatch(setSelectedListingId(''))

      const elements = document?.getElementsByName('list-top')

      if (elements.length > 0) {
        elements[0].scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }

      dispatch(setFilter({ boundingBox, page: 1 }))
      dispatch(
        searchListingsForMapMarkers(customerId, {
          ...filter,
          boundingBox,
        })
      )

      dispatch(
        searchListingsForListView(customerId, {
          ...filter,
          boundingBox,
        })
      )

      setMapState({
        ...mapState,
        bounds: state.bounds,
        center: {
          lat: state.center?.lat,
          lng: state.center?.lng,
        },
        zoom: state.zoom <= 7 ? 7 : state.zoom,
      })
    } else {
      setFirstRender(false)
      setMapState({
        ...mapState,
        bounds: state.bounds,
        center: {
          lat: state.center?.lat,
          lng: state.center?.lng,
        },
        zoom: state.zoom <= 7 ? 7 : state.zoom,
      })
    }
  }

  const { center, zoom } = mapState

  return (
    <div
      id='map-web-container'
      ref={mapRefContainer}
      className={styles.mapContainer}
    >
      <div id='map-wrapper' ref={mapRef} className={styles.map}>
        <Suspense fallback={<LoadingContainer />}>
          <PropertyMap
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={({ map, maps }) =>
              setGoogleMapsInternals({ map, maps })
            }
            bootstrapURLKeys={googleApiKey}
            center={center}
            zoom={zoom}
            options={mapOptions}
            onChange={handleChange}
          >
            {clusters?.map(cluster => {
              const [longitude, latitude] = cluster.geometry.coordinates
              const {
                cluster: isCluster,
                point_count: numberOfPointsInCluster,
              } = cluster.properties

              if (isCluster) {
                return (
                  <Cluster
                    key={`cluster-${cluster.id}`}
                    id={cluster.id}
                    numberOfPointsInCluster={numberOfPointsInCluster}
                    onClick={onClusterClick}
                    lat={latitude}
                    lng={longitude}
                  />
                )
              } else {
                return (
                  <Marker
                    key={`marker-${cluster.properties.id}`}
                    focusedListing={focusedMarker}
                    lat={latitude}
                    listing={cluster.properties}
                    lng={longitude}
                    onClick={onMarkerClick}
                  />
                )
              }
            })}
          </PropertyMap>
        </Suspense>
      </div>
    </div>
  )
}

ListingsMap.propTypes = {
  filter: PropTypes.shape({
    location: PropTypes.shape({
      id: PropTypes.string,
      lat: PropTypes.number,
      lon: PropTypes.number,
      name: PropTypes.string,
    }),
    minBathroomsCount: PropTypes.number,
    minBedroomsCount: PropTypes.number,
    minPriceCents: PropTypes.number,
    maxPriceCents: PropTypes.number,
    minSqft: PropTypes.number,
    maxSqft: PropTypes.number,
    minYearBuilt: PropTypes.string,
    maxYearBuilt: PropTypes.string,
    propertyTypes: PropTypes.arrayOf(PropTypes.string),
  }),
  focusedMarker: PropTypes.shape({
    id: PropTypes.string,
  }),
  onMarkerClick: PropTypes.func,
  listings: PropTypes.array,
}

export default ListingsMap
